// Template 4 — "Atelier" — a premium editorial landing page with a distinct
// two-column layout: a persistent left sidebar (course video, price, enroll, jump
// nav) beside a scrolling content column. Warm bone canvas, Playfair Display
// serif, hairline rules and a luxe burgundy accent. Fully theme-driven.
import { Fragment, useState } from "react";
import RichText from "../RichText";
import { Icon, Stars, splitTitle, rgba, shade, useGoogleFonts, usePreviewVideo, VideoModal, HeroVideo, SOCIAL_ICONS, orderedSections, skillIconD, PolicyLinks, GuaranteeBadge, Reveal, LessonIcon } from "./shared";

const SIGNATURE2 = "#C99A2E";
const SIGNATURE = "#9F1239";     // luxe burgundy — refined on bone
const BG = "#f5f3ee";            // warm bone canvas
const PANEL = "#ffffff";
const SOFT = "#ece6da";          // alt fill
const INK = "#221d1b";           // warm near-black text
const MUT = "#6f665f";           // muted text
const LINE = "#e4ddd0";          // hairline rules
const DARK = "#1b1613";          // ink bands (guarantee / CTA)
const SERIF = "'Playfair Display', Georgia, serif";
const SANS = "'Inter', system-ui, sans-serif";

export default function Template4({ course, landing, instructor, modules, themeColor, themeColor2, canView, labels = {}, onEnroll, policies }) {
  useGoogleFonts("https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,500;0,600;0,700;0,800;1,600;1,700&family=Inter:wght@400;500;600;700&display=swap");
  const vid = usePreviewVideo();
  const A = themeColor || SIGNATURE;
  const AD = shade(A, -0.12);
  const B = themeColor2 || SIGNATURE2;   // secondary branding accent
  const grad = `linear-gradient(135deg, ${A}, ${AD})`;
  const pal = { A, AD, B, grad };

  const header = landing.header || {};
  const t = splitTitle(header.headline || course.name);
  const sub = header.subheadline || course.short_description || "";
  const isPaid = course.access_type === 2;
  const priceLabel = isPaid && course.price > 0 ? course.price : (labels.free || "Free");
  const ctaLabel = isPaid ? (labels.buy_now || "Buy Now") : (labels.enroll_now || "Enroll Now");
  const statusLabel = isPaid ? "Premium course" : course.access_type === 3 ? (canView ? "Unlocked" : "Members only") : "Free course";
  const lessonCount = course.total_lessons || modules.reduce((a, m) => a + (m.lessons?.length || 0), 0);

  const order = orderedSections(landing.section_order);
  const navLinks = [
    landing.about?.show !== false && (landing.description || course.description) && { href: "#t4-about", label: "About" },
    landing.skill?.show && landing.skill.list?.length > 0 && { href: "#t4-skill", label: "Skills" },
    modules.length > 0 && landing.course_include?.show !== false && { href: "#t4-curriculum", label: labels.curriculum || "Curriculum" },
    instructor?.name && landing.instructor?.show !== false && { href: "#t4-instructor", label: "Instructor" },
    landing.testimonials?.show && landing.testimonials.list?.length > 0 && { href: "#t4-reviews", label: labels.reviews || "Reviews" },
    landing.faq?.show && landing.faq.list?.length > 0 && { href: "#t4-faq", label: labels.faq || "FAQ" },
  ].filter(Boolean);

  const sideStats = [
    course.star_rating > 0 && { k: labels.rating || "Rating", v: `${Number(course.star_rating).toFixed(1)} / 5` },
    course.total_lessons > 0 && { k: labels.lessons || "Lessons", v: course.total_lessons },
    modules.length > 0 && { k: labels.modules || "Modules", v: modules.length },
    { k: "Access", v: "Lifetime" },
  ].filter(Boolean);

  const blocks = {
    about: landing.about?.show !== false && (landing.description || course.description) && (
      <Reveal><Sec id="t4-about" kicker="Overview" title="About this course" pal={pal}>
        <div className="prose prose-sm mt-6 max-w-none leading-relaxed" style={{ color: MUT }}><RichText html={landing.description || course.description} /></div>
      </Sec></Reveal>
    ),
    instructor: landing.instructor?.show !== false && instructor?.name && (
      <Reveal><div id="t4-instructor"><Sec kicker="Instructor" title={labels.meet_instructor || "Meet the instructor"} pal={pal}>
        <div className="mt-8 grid gap-8 sm:grid-cols-[200px_1fr]">
          <div className="relative mx-auto w-full max-w-[200px]">
            <div className="absolute -bottom-3 -right-3 h-full w-full" style={{ border: `1px solid ${A}` }} />
            <div className="relative aspect-[4/5] w-full overflow-hidden" style={{ background: SOFT, border: `1px solid ${LINE}` }}>
              {instructor.profile_image ? <img src={instructor.profile_image} alt="" className="h-full w-full object-cover" /> : <span className="flex h-full w-full items-center justify-center text-7xl italic" style={{ fontFamily: SERIF, color: rgba(A, 0.3) }}>{instructor.name.charAt(0)}</span>}
            </div>
          </div>
          <div className="flex flex-col justify-center text-center sm:text-left">
            <span className="text-[0.7rem] font-bold uppercase tracking-[0.2em]" style={{ color: A }}>Course Instructor</span>
            <h3 className="mt-2 text-[2rem] leading-tight" style={{ fontFamily: SERIF, fontWeight: 700, color: INK }}>{instructor.name}</h3>
            {instructor.bio && <p className="mt-4 max-w-lg text-[15px] leading-relaxed" style={{ color: MUT }}>{instructor.bio}</p>}
          </div>
        </div>
      </Sec></div></Reveal>
    ),
    skill: landing.skill?.show && landing.skill.list?.length > 0 && (
      <Reveal><Sec id="t4-skill" kicker={labels.what_youll_learn || "What you'll learn"} title={landing.skill.heading || "Skills you will walk away with"} pal={pal}>
        <div className="mt-8 grid gap-x-10 gap-y-5 sm:grid-cols-2">
          {landing.skill.list.map((s, i) => (
            <div key={i} className="flex items-start gap-4 border-t pt-5" style={{ borderColor: LINE }}>
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white" style={{ background: A }}><Icon d={skillIconD(landing.skill.icon)} className="h-4 w-4" stroke={2.2} /></span>
              <span className="text-[15px] leading-snug" style={{ color: INK }}>{s}</span>
            </div>
          ))}
        </div>
        {landing.skill.long_desc && <div className="rich-content mt-6 text-[15px] leading-relaxed" style={{ color: MUT }} dangerouslySetInnerHTML={{ __html: landing.skill.long_desc }} />}
      </Sec></Reveal>
    ),
    include: modules.length > 0 && landing.course_include?.show !== false && (
      <Reveal><div id="t4-curriculum"><Sec kicker={labels.curriculum || "Curriculum"} title={labels.what_inside_course || "What's inside the course"} sub={`${modules.length} ${labels.modules || "Modules"} · ${lessonCount} ${labels.lessons || "Lessons"}`} pal={pal}>
        <Accordion modules={modules} pal={pal} labels={labels} />
      </Sec></div></Reveal>
    ),
    testimonial: <div id="t4-reviews"><Testimonials testimonials={landing.testimonials} pal={pal} labels={labels} /></div>,
    moneyback: isPaid && <Guarantee guarantee={landing.guarantee} pal={pal} />,
    faq: <div id="t4-faq"><Faq faq={landing.faq} pal={pal} labels={labels} /></div>,
  };

  return (
    <div className="min-h-screen" style={{ background: BG, color: INK, fontFamily: SANS }}>
      {/* TOP BAR */}
      <div className="sticky top-0 z-50 border-b backdrop-blur" style={{ background: rgba(BG, 0.85), borderColor: LINE }}>
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
          <a href="#top" className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center text-white" style={{ background: A }}><Icon d="M12 14l9-5-9-5-9 5 9 5z M12 14l6.16-3.42a12 12 0 01.34 3.42c0 3-3 5-6.5 5S5.5 17 5.5 14a12 12 0 01.34-3.42L12 14z" className="h-4 w-4" /></span>
            <span className="max-w-[42vw] truncate text-[1.15rem] sm:max-w-sm" style={{ fontFamily: SERIF, fontWeight: 700 }}>{course.name}</span>
          </a>
          <button onClick={onEnroll} className="rounded-full px-5 py-2 text-[0.72rem] font-bold uppercase tracking-[0.14em] text-white transition hover:-translate-y-0.5" style={{ background: A }}>{ctaLabel}</button>
        </div>
      </div>

      {/* HERO — full width */}
      <header id="top" className="relative overflow-hidden border-b" style={{ borderColor: LINE }}>
        {header.bg_image ? <img src={header.bg_image} alt="" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.07]" /> : null}
        <div className="pointer-events-none absolute right-[-3rem] top-[-5rem] select-none text-[20rem] italic leading-none opacity-[0.05]" style={{ fontFamily: SERIF, color: A }}>{course.name.charAt(0)}</div>
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-5 py-16 sm:px-8 lg:grid-cols-[1.05fr_.95fr] lg:gap-12 lg:py-24">
          <div>
            <div className="flex items-center gap-3">
              <span className="h-px w-10" style={{ background: B }} />
              <span className="text-[0.68rem] font-bold uppercase tracking-[0.24em]" style={{ color: A }}>{statusLabel}</span>
            </div>
            <h1 className="mt-6 text-[3rem] leading-[0.98] sm:text-[4.4rem]" style={{ fontFamily: SERIF, fontWeight: 700 }}>
              {t.before} <em style={{ fontStyle: "italic", fontWeight: 600, color: A }}>{t.accent}</em> {t.after}
            </h1>
            {sub && <p className="mt-6 max-w-lg text-[16px] leading-relaxed" style={{ color: MUT }}>{sub}</p>}
          </div>
          {landing.header?.show !== false && (
            <div className="relative">
              <div className="absolute -bottom-3 -right-3 h-full w-full" style={{ border: `1px solid ${A}` }} />
              <div className="relative bg-white p-2.5 shadow-[0_40px_70px_-40px_rgba(33,29,24,.45)]" style={{ border: `1px solid ${LINE}` }}>
                <div className="overflow-hidden">
                  <HeroVideo video={landing.video} poster={course.course_image} accent={A} className="aspect-video w-full" style={{ background: DARK }} onExpand={vid.play} premium />
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* MAIN — sidebar + content */}
      <div className="mx-auto max-w-7xl lg:grid lg:grid-cols-[370px_1fr]">
        {/* SIDEBAR */}
        <aside className="border-b px-5 py-10 sm:px-8 lg:border-b-0 lg:border-r lg:py-14" style={{ borderColor: LINE }}>
          <div className="space-y-6 lg:sticky lg:top-24">
            {/* enroll card */}
            <div className="overflow-hidden bg-white" style={{ border: `1px solid ${LINE}`, boxShadow: "0 24px 50px -38px rgba(33,29,24,.4)" }}>
              <div className="p-6" style={{ background: `linear-gradient(160deg, ${rgba(A, 0.06)}, ${PANEL})` }}>
                <span className="text-[0.66rem] font-bold uppercase tracking-[0.2em]" style={{ color: A }}>{statusLabel}</span>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="text-[3.2rem] leading-none" style={{ fontFamily: SERIF, fontWeight: 700, color: INK }}>{priceLabel}</span>
                  {isPaid && <span className="text-xs uppercase tracking-wide" style={{ color: MUT }}>one-time</span>}
                </div>
                <button onClick={onEnroll} className="group mt-5 flex w-full items-center justify-center gap-2 rounded-full py-3.5 text-[0.78rem] font-bold uppercase tracking-[0.14em] text-white transition hover:-translate-y-0.5" style={{ background: A, boxShadow: `0 12px 28px ${rgba(A, 0.35)}` }}>{ctaLabel} <Icon d="M13 7l5 5m0 0l-5 5m5-5H6" className="h-4 w-4 transition-transform group-hover:translate-x-1" /></button>
                {isPaid && landing.guarantee?.show && <p className="mt-3 text-center text-[12px] leading-relaxed" style={{ color: MUT }}><span className="font-semibold" style={{ color: INK }}>{landing.guarantee.days || 30}-Day {labels.money_back_guarantee || "Money-Back Guarantee"}.</span> {labels.not_satisfied || "Not satisfied? Get a full refund."}</p>}
              </div>
              <ul className="border-t px-6 py-1" style={{ borderColor: LINE }}>
                {sideStats.map((s, i) => (
                  <li key={i} className={`flex items-center justify-between py-3 ${i > 0 ? "border-t" : ""}`} style={{ borderColor: LINE }}>
                    <span className="text-[0.72rem] font-semibold uppercase tracking-[0.12em]" style={{ color: MUT }}>{s.k}</span>
                    <span className="text-sm font-bold" style={{ color: INK }}>{s.v}</span>
                  </li>
                ))}
              </ul>
            </div>
            {navLinks.length > 0 && (
              <nav className="hidden px-1 lg:block">
                <span className="text-[0.66rem] font-bold uppercase tracking-[0.2em]" style={{ color: MUT }}>On this page</span>
                <ul className="mt-3 space-y-1">
                  {navLinks.map((l) => <li key={l.href}><a href={l.href} className="group flex items-center gap-2.5 py-1.5 text-sm font-medium transition hover:text-black" style={{ color: MUT }}><span className="h-px w-4 transition-all group-hover:w-6" style={{ background: A }} />{l.label}</a></li>)}
                </ul>
              </nav>
            )}
          </div>
        </aside>

        {/* CONTENT */}
        <main className="px-5 py-14 sm:px-8 lg:py-20">
          <div className="space-y-20">
            {order.map((id) => <Fragment key={id}>{blocks[id]}</Fragment>)}
          </div>
        </main>
      </div>

      {/* FINAL CTA — ink band */}
      {landing.cta?.show !== false && (
        <section className="relative overflow-hidden border-t" style={{ background: DARK, borderColor: LINE }}>
          <div className="pointer-events-none absolute inset-0" style={{ backgroundImage: `radial-gradient(60% 100% at 50% 0%, ${rgba(A, 0.35)}, transparent 60%)` }} />
          <div className="relative mx-auto max-w-4xl px-5 py-20 text-center sm:px-8">
            <h2 className="text-[2.4rem] leading-tight text-white sm:text-[3.4rem]" style={{ fontFamily: SERIF, fontWeight: 700 }}>
              {landing.cta?.heading ? landing.cta.heading : <>{t.before} <em style={{ fontStyle: "italic", color: shade(A, 0.4) }}>{t.accent}</em> {t.after}</>}
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-[15px] leading-relaxed text-white/60">{landing.cta?.subheading || sub || `Join ${course.name} and start learning today.`}</p>
            <button onClick={onEnroll} className="group mt-9 inline-flex items-center gap-2.5 rounded-full px-8 py-4 text-[0.8rem] font-bold uppercase tracking-[0.14em] text-white transition hover:-translate-y-0.5" style={{ background: A, boxShadow: `0 14px 34px ${rgba(A, 0.5)}` }}>{ctaLabel} <Icon d="M13 7l5 5m0 0l-5 5m5-5H6" className="h-4 w-4 transition-transform group-hover:translate-x-1" /></button>
          </div>
        </section>
      )}

      {/* FOOTER */}
      <footer className="border-t" style={{ background: BG, borderColor: LINE }}>
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-5 px-5 py-10 sm:flex-row sm:px-8">
          <div className="flex items-center gap-2.5">
            {Object.keys(SOCIAL_ICONS).filter((k) => landing.social?.[k]).map((k) => (
              <a key={k} href={landing.social[k]} target="_blank" rel="noopener noreferrer" aria-label={k} className="flex h-9 w-9 items-center justify-center transition hover:text-white" style={{ border: `1px solid ${LINE}`, color: MUT }} onMouseEnter={(e) => { e.currentTarget.style.background = A; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = A; }} onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = MUT; e.currentTarget.style.borderColor = LINE; }}>
                <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current"><path d={SOCIAL_ICONS[k]} /></svg>
              </a>
            ))}
          </div>
          <PolicyLinks policies={policies} accent={A} />
        </div>
      </footer>

      <VideoModal video={landing.video} open={vid.open} onClose={vid.close} />
    </div>
  );
}

/* -------------------------------- helpers -------------------------------- */
function guaranteeDefaultHtml(days) {
  return `<p>We believe in the value of this course and are confident it will help you achieve your goals. That's why we offer a full ${days}-day risk-free money-back guarantee.</p><p>Go through the content, explore the lessons, and put what you learn into action. If at any point within the first ${days} days you feel the course isn't right for you or doesn't meet your expectations, simply reach out to us.</p><p>We'll process your refund promptly—no questions asked.</p><p>There's absolutely no risk in getting started today, only the opportunity to learn, grow, and succeed with complete peace of mind.</p>`;
}

function Sec({ id, kicker, title, sub, pal, children }) {
  return (
    <section id={id} className="scroll-mt-24">
      <span className="text-[0.7rem] font-bold uppercase tracking-[0.2em]" style={{ color: pal.A }}>{kicker}</span>
      <h2 className="mt-2 text-[2rem] leading-[1.06] sm:text-[2.7rem]" style={{ fontFamily: SERIF, fontWeight: 700, color: INK }}>{title}</h2>
      {sub && <p className="mt-2 text-sm" style={{ color: MUT }}>{sub}</p>}
      <span className="mt-6 flex w-full items-center">
        <span className="h-px w-12 shrink-0" style={{ background: pal.B }} />
        <span className="h-px flex-1" style={{ background: LINE }} />
      </span>
      {children}
    </section>
  );
}

function Accordion({ modules, pal, labels }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="mt-2">
      {modules.map((m, mi) => {
        const isOpen = open === mi;
        return (
          <div key={mi} className="border-b" style={{ borderColor: LINE }}>
            <button onClick={() => setOpen(isOpen ? -1 : mi)} className="flex w-full items-center gap-5 py-6 text-left">
              <span className="text-2xl italic tabular-nums" style={{ fontFamily: SERIF, fontWeight: 600, color: isOpen ? pal.A : rgba(INK, 0.35) }}>{String(mi + 1).padStart(2, "0")}</span>
              <span className="flex-1 text-[1.15rem]" style={{ fontFamily: SERIF, fontWeight: 600, color: INK }}>{m.name}</span>
              <span className="hidden text-xs uppercase tracking-wide sm:block" style={{ color: MUT }}>{m.lessons?.length || 0} {labels.lessons || "lessons"}</span>
              <Icon d={isOpen ? "M5 12h14" : "M12 5v14m7-7H5"} className="h-4 w-4 shrink-0" style={{ color: isOpen ? pal.A : INK }} />
            </button>
            <div className="grid transition-all duration-300" style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}>
              <div className="overflow-hidden">
                <ul className="pb-6 pl-12">
                  {(m.lessons || []).map((l, li) => (
                    <li key={li} className="flex items-center gap-3 border-t py-3" style={{ borderColor: LINE }}>
                      <LessonIcon lesson={l} color={pal.A} muted={rgba(INK, 0.3)} className="h-4 w-4 shrink-0" />
                      <span className="flex-1 truncate text-sm" style={{ color: l.locked ? rgba(INK, 0.4) : MUT }}>{l.title}</span>
                      {l.completed && <span className="text-xs font-semibold" style={{ color: pal.A }}>Done</span>}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Testimonials({ testimonials, pal, labels }) {
  const list = (testimonials?.list || []).filter((t) => t.quote || t.name);
  if (!testimonials?.show || !list.length) return null;
  return (
    <Reveal>
      <Sec kicker={labels.testimonials || "Testimonials"} title={testimonials.heading || "What students are saying"} pal={pal}>
        <div className="mt-8 grid gap-6 sm:grid-cols-2">
          {list.map((t, i) => (
            <figure key={i} className="flex flex-col p-7" style={{ background: PANEL, border: `1px solid ${LINE}` }}>
              <span className="text-6xl leading-none" style={{ fontFamily: SERIF, color: pal.A }}>&ldquo;</span>
              <Stars value={t.rating} empty="#e2dccf" color={pal.B} className="-mt-3 h-4 w-4" />
              <blockquote className="mt-4 flex-1 text-[1.1rem] leading-relaxed" style={{ fontFamily: SERIF, fontWeight: 500, color: INK }}>{t.quote}</blockquote>
              <figcaption className="mt-6 flex items-center gap-3 border-t pt-5" style={{ borderColor: LINE }}>
                <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden text-sm font-bold text-white" style={{ background: pal.A }}>{t.avatar ? <img src={t.avatar} alt="" className="h-full w-full object-cover" /> : (t.name || "?").charAt(0).toUpperCase()}</span>
                <span className="min-w-0"><span className="block truncate text-sm font-semibold" style={{ color: INK }}>{t.name}</span>{t.role && <span className="block truncate text-xs" style={{ color: MUT }}>{t.role}</span>}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </Sec>
    </Reveal>
  );
}

function Guarantee({ guarantee, pal }) {
  if (!guarantee?.show) return null;
  const days = guarantee.days || 30;
  return (
    <Reveal>
      <section className="relative overflow-hidden px-7 py-12 sm:px-10" style={{ background: DARK }}>
        <div className="pointer-events-none absolute inset-0" style={{ backgroundImage: `radial-gradient(60% 120% at 88% 50%, ${rgba(pal.A, 0.3)}, transparent 60%)` }} />
        {guarantee.bg_image ? <img src={guarantee.bg_image} alt="" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-20" /> : null}
        <div className="relative flex flex-col items-center gap-9 text-center sm:flex-row sm:text-left">
          <div className="order-2 flex-1 sm:order-1">
            <h3 className="text-[1.7rem] leading-tight text-white sm:text-[2.1rem]" style={{ fontFamily: SERIF, fontWeight: 700 }}>{guarantee.heading || `${days}-Day Risk-Free Money-Back Guarantee`}</h3>
            <div className="rich-content mt-3 text-[15px] leading-relaxed text-white/65" dangerouslySetInnerHTML={{ __html: guarantee.description || guaranteeDefaultHtml(days) }} />
          </div>
          <div className="order-1 shrink-0 sm:order-2">
            {guarantee.image
              ? <img src={guarantee.image} alt="" className="h-40 w-40 object-contain drop-shadow-2xl" />
              : <GuaranteeBadge variant={guarantee.badge || 1} days={days} accent={pal.A} className="h-40 w-40 drop-shadow-2xl sm:h-44 sm:w-44" />}
          </div>
        </div>
      </section>
    </Reveal>
  );
}

function Faq({ faq, pal, labels }) {
  const list = (faq?.list || []).filter((f) => f.q);
  const [open, setOpen] = useState(-1);
  if (!faq?.show || !list.length) return null;
  return (
    <Reveal>
      <Sec kicker={labels.faq || "FAQ"} title={faq.heading || "Frequently asked questions"} pal={pal}>
        <div className="mt-2">
          {list.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className="border-b" style={{ borderColor: LINE }}>
                <button onClick={() => setOpen(isOpen ? -1 : i)} className="flex w-full items-center gap-5 py-5 text-left">
                  <span className="flex-1 text-[1.1rem]" style={{ fontFamily: SERIF, fontWeight: 600, color: INK }}>{f.q}</span>
                  <Icon d={isOpen ? "M5 12h14" : "M12 5v14m7-7H5"} className="h-4 w-4 shrink-0" style={{ color: isOpen ? pal.A : INK }} />
                </button>
                <div className="grid transition-all duration-300" style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}>
                  <div className="overflow-hidden"><p className="pb-5 text-[15px] leading-relaxed" style={{ color: MUT }}>{f.a}</p></div>
                </div>
              </div>
            );
          })}
        </div>
      </Sec>
    </Reveal>
  );
}
